import { DocSection } from './types';

export const helpSections: DocSection[] = [
  // Getting Started
  {
    id: 'gs-login',
    title: 'Logging In',
    category: 'Getting Started',
    content: `To log in to BlueHMS:
1. Enter your username or email
2. Enter your password
3. Tap **Login**

If you forget your password, ask your administrator to reset it.`,
    keywords: ['login', 'password', 'authenticate'],
  },
  {
    id: 'gs-dashboard',
    title: 'Dashboard Overview',
    category: 'Getting Started',
    content: `The Dashboard shows your key metrics:
- Account balance
- Active wallets
- Recent transactions
- Quick access to common tasks

Tap any card to dive deeper into that module.`,
    keywords: ['dashboard', 'home', 'overview'],
  },
  {
    id: 'gs-drawer',
    title: 'Using the Drawer Menu',
    category: 'Getting Started',
    content: `Tap the **hamburger menu** (three lines) in the top-left to open the drawer:
- Browse all available modules
- Access settings and help
- Manage your profile

Tap outside the drawer or the backdrop to close it.`,
    keywords: ['menu', 'drawer', 'navigation'],
  },
  {
    id: 'gs-bottomnav',
    title: 'Bottom Navigation',
    category: 'Getting Started',
    content: `The bottom tabs provide quick access to major sections:
- **Home/POS** - Point of Sale outlet launcher
- **Rooms** - Room management and check-in
- **Transactions** - Billing and posting
- **Reports** - View analytics
- **Settings** - App configuration

Tap any tab to jump to that section.`,
    keywords: ['bottom nav', 'tabs', 'navigation'],
  },

  // Front Office
  {
    id: 'fo-checkin',
    title: 'Checking In a Guest',
    category: 'Front Office',
    content: `To check in a guest:
1. Go to **Room Status** or **Check In**
2. Select a vacant room
3. Enter guest details (name, mobile, email)
4. Choose room rate and any add-ons
5. Select payment mode
6. Tap **Confirm Check In**

The room status will update to **Occupied**.`,
    keywords: ['check in', 'guest', 'arrival'],
  },
  {
    id: 'fo-checkout',
    title: 'Checking Out a Guest',
    category: 'Front Office',
    content: `To check out a guest:
1. Go to **Room Status** or **Check Out**
2. Select the occupied room
3. Review the bill (posting, extras, taxes)
4. Choose settlement mode (cash, card, wallet, etc.)
5. Tap **Confirm Checkout**

The guest is checked out and the room becomes available.`,
    keywords: ['check out', 'settlement', 'bill'],
  },
  {
    id: 'fo-roomstatus',
    title: 'Room Status Board',
    category: 'Front Office',
    content: `The **Room Status** board shows all rooms with their current state:
- **Occupied** (blue) - Guest checked in
- **Vacant** (green) - Ready to book
- **Dirty** (red) - Needs housekeeping
- **Block** (gray) - Unavailable
- **Repair** (yellow) - Under maintenance

Use filters to view specific room types or statuses.

Tap **Public Places** to see common area status.`,
    keywords: ['room status', 'availability', 'board'],
  },
  {
    id: 'fo-posting',
    title: 'Room Posting',
    category: 'Front Office',
    content: `Posting is how you add charges to a guest's bill during their stay:
1. Go to **Posting**
2. Select the room
3. Choose an item (food, laundry, minibar, etc.)
4. Enter quantity and confirm

The charge appears on the bill at checkout. Some posting items may require approval.`,
    keywords: ['posting', 'charges', 'bill', 'add to bill'],
  },
  {
    id: 'fo-advance',
    title: 'Taking Advance Payment',
    category: 'Front Office',
    content: `To take advance payment for a future booking:
1. Go to **Advance Receipt**
2. Enter guest details
3. Enter amount and choose payment mode
4. Add note/reference if needed
5. Tap **Confirm**

A receipt is generated. When the guest checks in, the advance is credited.`,
    keywords: ['advance', 'payment', 'receipt', 'deposit'],
  },
  {
    id: 'fo-extend',
    title: 'Extending a Stay',
    category: 'Front Office',
    content: `To extend a guest's stay:
1. Go to **Room Status** and find the room
2. Long-press the room card or tap its action menu
3. Select **Extend Stay**
4. Choose new checkout date
5. Confirm rate and charges

The guest's checkout date is updated.`,
    keywords: ['extend', 'extend stay', 'checkout date'],
  },
  {
    id: 'fo-linkrooms',
    title: 'Linking Multiple Rooms',
    category: 'Front Office',
    content: `To link rooms (e.g., a guest books two adjacent rooms):
1. Go to **Link Rooms**
2. Select the first room with the guest
3. Select additional rooms to link
4. Confirm

Linked rooms share one bill and checkout together. You can unlink later if needed.`,
    keywords: ['link rooms', 'multiple rooms', 'suite'],
  },
  {
    id: 'fo-housekeeping',
    title: 'Housekeeping Task Management',
    category: 'Front Office',
    content: `To request housekeeping:
1. Go to **Housekeeping**
2. Select the room or public place
3. Choose task type (cleaning, linen change, repair, etc.)
4. Add any special instructions
5. Tap **Assign**

Housekeeping staff can see pending tasks and mark them complete.`,
    keywords: ['housekeeping', 'cleaning', 'task'],
  },
  {
    id: 'fo-grc',
    title: 'Guest Check-In Form (GRC)',
    category: 'Front Office',
    content: `The Guest Registration Card (GRC) captures guest details:
1. During check-in, fill guest name, address, ID proof
2. Enter contact info and preferences
3. Note any special requests
4. Confirm

This info is used for tracking and communication.`,
    keywords: ['guest registration', 'GRC', 'registration card'],
  },

  // Point of Sale
  {
    id: 'pos-tables',
    title: 'Managing Tables',
    category: 'Point of Sale',
    content: `In the POS outlet:
1. Tap **Tables** to view the floor layout
2. Tap a table to see its status:
   - **Vacant** (white) - available
   - **Occupied** (orange) - guest seated
   - **In Bill** (yellow) - ready for payment
3. Tap a table to start taking orders

You can merge tables or split bills as needed.`,
    keywords: ['table', 'POS', 'outlet', 'restaurant'],
  },
  {
    id: 'pos-products',
    title: 'Taking Orders',
    category: 'Point of Sale',
    content: `To take an order:
1. Tap a vacant table to start a KOT (Kitchen Order Ticket)
2. Browse **Products** by category
3. Tap items to add them, adjust quantity
4. You can:
   - **Print KOT** to send to kitchen
   - **Hold KOT** to add items later
   - **Serve** items as they're ready
5. When done, move to **Bill** to finalize

Prices, taxes, and discounts are calculated automatically.`,
    keywords: ['order', 'product', 'KOT', 'food'],
  },
  {
    id: 'pos-bill',
    title: 'Settling a Bill',
    category: 'Point of Sale',
    content: `To settle (close) a bill:
1. Select the table or bill
2. Review the itemized list
3. Apply any discounts if authorized
4. Choose **Payment Mode**:
   - Cash
   - Card
   - Wallet
   - Credit
   - Complimentary
5. Confirm and print receipt

The bill is closed and table becomes vacant.`,
    keywords: ['bill', 'payment', 'settle', 'receipt'],
  },
  {
    id: 'pos-kotcancel',
    title: 'Canceling Items or KOTs',
    category: 'Point of Sale',
    content: `To cancel items:
1. Go to **Cancel KOT**
2. Select the KOT to modify
3. Choose items to cancel
4. Enter cancellation reason (if required)
5. Confirm

Canceled items won't appear on the final bill.`,
    keywords: ['cancel', 'KOT', 'item', 'remove'],
  },
  {
    id: 'pos-split',
    title: 'Splitting a Bill',
    category: 'Point of Sale',
    content: `To split a bill between guests:
1. Go to **Change Table** (bill options)
2. Select **Split Bill**
3. Choose items to go to each guest
4. Confirm

Each guest pays their portion separately.`,
    keywords: ['split', 'bill', 'payment', 'multiple guests'],
  },

  // Store
  {
    id: 'store-indent',
    title: 'Creating a Purchase Indent',
    category: 'Store',
    content: `An indent is a purchase request:
1. Go to **Store > Indent**
2. Select supplier
3. Add products and quantities
4. Optionally attach delivery/reference documents
5. Tap **Submit for Approval**

Once approved, it can be converted to a purchase order.`,
    keywords: ['indent', 'purchase', 'request', 'supplier'],
  },
  {
    id: 'store-po',
    title: 'Creating a Purchase Order',
    category: 'Store',
    content: `A Purchase Order (PO) is the formal order to a supplier:
1. Go to **Store > Purchase Order**
2. Select supplier and their rate
3. Add items and quantities
4. Review total with taxes
5. Tap **Confirm Order**

An order reference is generated for tracking.`,
    keywords: ['purchase order', 'PO', 'supplier', 'order'],
  },
  {
    id: 'store-purchase',
    title: 'Receiving Purchases',
    category: 'Store',
    content: `When goods arrive:
1. Go to **Store > Purchase**
2. Select the PO
3. Enter received quantities (may differ from ordered)
4. Mark any damaged items
5. Tap **Confirm Receipt**

Stock is updated and a GRN (Goods Receipt Note) is created.`,
    keywords: ['purchase', 'receipt', 'GRN', 'goods', 'inventory'],
  },

  // Housekeeping
  {
    id: 'hk-tasks',
    title: 'Housekeeping Tasks',
    category: 'Housekeeping',
    content: `Housekeeping staff view and manage tasks:
1. Go to **Housekeeping**
2. View assigned tasks (rooms, public places)
3. Tap a task to see details and special instructions
4. Mark **In Progress** when starting
5. Mark **Done** when completed

Task status updates in real-time for the front desk.`,
    keywords: ['housekeeping', 'task', 'room', 'cleaning'],
  },

  // Asset Management
  {
    id: 'asset-entry',
    title: 'Asset Purchase Entry',
    category: 'Asset Management',
    content: `To record a new asset:
1. Go to **Asset Management > Asset Purchase**
2. Enter asset details:
   - Item name and category
   - Purchase date and amount
   - Warranty period
3. Optionally attach invoice
4. Tap **Save**

Assets are tracked for maintenance and depreciation.`,
    keywords: ['asset', 'purchase', 'equipment', 'fixed asset'],
  },
  {
    id: 'asset-list',
    title: 'Asset List and Maintenance',
    category: 'Asset Management',
    content: `To view and manage assets:
1. Go to **Asset Management > Assets**
2. See all recorded assets
3. Tap an asset to view details:
   - Purchase history
   - Warranty expiry
   - Maintenance logs
4. Add maintenance notes or mark for repair

Helps track equipment lifespan and plan replacements.`,
    keywords: ['asset', 'list', 'maintenance', 'equipment'],
  },

  // Food Production
  {
    id: 'fp-production',
    title: 'Food Production Entry',
    category: 'Food Production',
    content: `To record food production:
1. Go to **Food Production**
2. Select a recipe or item
3. Enter quantity produced
4. Record time and staff assigned
5. Tap **Save**

Helps track food prep for planning and costing.`,
    keywords: ['food production', 'recipe', 'kitchen'],
  },

  // Other
  {
    id: 'other-notifications',
    title: 'Notifications',
    category: 'Other',
    content: `Notifications alert you to important events:
- Task assignments
- Guest requests
- Payment confirmations
- System alerts

Tap the bell icon to view your notification history. Long-press to mark as read.`,
    keywords: ['notification', 'alert', 'message'],
  },
  {
    id: 'other-authorizations',
    title: 'Pending Authorizations',
    category: 'Other',
    content: `Some actions need approval:
1. Go to **Pending Authorizations**
2. View requests (discounts, refunds, cancellations)
3. Tap to review details
4. Approve or reject

Once approved, the action proceeds. Rejected items return to the originator.`,
    keywords: ['authorization', 'approval', 'pending', 'manager'],
  },
  {
    id: 'other-revenue',
    title: 'Revenue Summary',
    category: 'Other',
    content: `The **Revenue Summary** shows daily/weekly/monthly metrics:
- Occupancy rate
- Revenue from rooms and POS
- Payment breakdowns
- Trending data

Swipe to change date range. Tap a segment to drill down.`,
    keywords: ['revenue', 'sales', 'report', 'dashboard'],
  },
  {
    id: 'faq-forgot-password',
    title: 'I forgot my password. What do I do?',
    category: 'FAQ',
    content: `Ask your administrator to reset it.`,
    keywords: ['password', 'forgot', 'login', 'reset'],
  },
  {
    id: 'faq-find-module',
    title: 'Where do I find a module or setting?',
    category: 'FAQ',
    content: `Tap the **hamburger menu** (three lines) at the top-left to open the drawer. It lists every module you have access to, plus settings and help.`,
    keywords: ['menu', 'drawer', 'module', 'find'],
  },
  {
    id: 'faq-no-module',
    title: 'A module I need is missing from the menu.',
    category: 'FAQ',
    content: `Menus follow your access rights. Ask your administrator to grant you access to that screen.`,
    keywords: ['missing', 'access', 'rights', 'menu'],
  },
  {
    id: 'faq-checkin',
    title: 'How do I check a guest in or out?',
    category: 'FAQ',
    content: `See **Checking In a Guest** and **Checking Out a Guest** under Front Office.`,
    keywords: ['check in', 'check out', 'guest', 'front office'],
  },
  {
    id: 'faq-settle',
    title: 'How do I settle a restaurant bill or cancel an item?',
    category: 'FAQ',
    content: `See **Settling a Bill** and **Canceling Items or KOTs** under Point of Sale.`,
    keywords: ['bill', 'settle', 'cancel', 'kot', 'pos'],
  },
  {
    id: 'faq-approvals',
    title: 'Where do I approve a discount or tariff request?',
    category: 'FAQ',
    content: `Open **Pending Authorizations**. A request only appears there if the requesting user does not already have the right to do it.`,
    keywords: ['approve', 'authorization', 'discount', 'manager'],
  },
  {
    id: 'faq-assets',
    title: 'How do I record a new asset purchase?',
    category: 'FAQ',
    content: `See **Asset Purchase Entry** under Asset Management.`,
    keywords: ['asset', 'purchase', 'entry'],
  },
];
